<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useEventStore } from '@/stores/useEventStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { computed } from 'vue'
import Avatar from '@/components/common/ui/Avatar.vue'
import SideUserCalendar from '../ui/SideUserCalendar.vue'
import ActivityTable from '@/components/features/activities/ActivityTable.vue'
import { watch } from 'vue'

const boostCoreStore = useBoostCorePermissionStore()
const eventStore = useEventStore()
const { eventsFromCurrentMonth, loadingEventsUser } = storeToRefs(eventStore)

const isMsal = computed(() => boostCoreStore.authMethod === 'msal')
const userEmail = computed(() => boostCoreStore.user?.email ?? null)
const userName = computed(() => {
  const u = boostCoreStore.user
  if (!u) return ''
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email
})

const month = new Intl.DateTimeFormat('fr-FR', { month: 'long' }).format(new Date())
const filterDate = new Intl.DateTimeFormat('fr-FR', { year: 'numeric', month: '2-digit' }).format(
  new Date(),
)

const numberOfR1P = computed(
  () =>
    eventsFromCurrentMonth.value?.filter(
      (event) =>
        Array.isArray(event.categIds) &&
        event.categIds?.some((category: { name: string }) => category.name === 'R1P'),
    ).length || 0,
)

const numberOfR1C = computed(
  () =>
    eventsFromCurrentMonth.value?.filter(
      (event) =>
        Array.isArray(event.categIds) &&
        event.categIds?.some((category: { name: string }) => category.name === 'R1C'),
    ).length || 0,
)

watch(
  () => boostCoreStore.user,
  async (newVal) => {
    if (newVal?.email && isMsal.value) {
      await eventStore.getEventsByUserEmailAndCurrentMonth()
    }
  },
  { immediate: true },
)
</script>

<template>
  <div class="p-4 py-3 lg:my-4 lg:py-4">
    <div class="flex flex-col justify-center items-center gap-4">
      <Avatar :email="userEmail" :size="'120'" v-if="userEmail" />
      <div v-else class="skeleton rounded-full opacity-30 w-[120px] h-[120px]"></div>
      <p class="text-lg font-bold">{{ userName }}</p>
      <div
        class="flex flex-col gap-4 bg-base-100 px-3 py-2 rounded-2xl w-full"
        v-if="!loadingEventsUser"
      >
        <div class="flex flex-row gap-2">
          <p class="text-xl font-bold capitalize mr-2">{{ month }}</p>
          <RouterLink
            :to="`/diary?category=R1P&type=list&date=${filterDate}`"
            class="card bg-primary text-primary-content px-2 rounded-lg w-full"
          >
            <p>R1P</p>
            <p class="m-0 pe-1 pb-1 text-4xl font-bold text-right">{{ numberOfR1P }}</p>
          </RouterLink>
          <RouterLink
            :to="`/diary?category=R1C&type=list&date=${filterDate}`"
            class="card bg-accent text-accent-content px-2 rounded-lg w-full"
          >
            <p>R1C</p>
            <p class="m-0 pe-1 pb-1 text-4xl font-bold text-right">{{ numberOfR1C }}</p>
          </RouterLink>
        </div>
      </div>
      <div v-else class="skeleton opacity-70 w-full h-24"></div>
      <SideUserCalendar />
      <activity-table> </activity-table>
    </div>
  </div>
</template>
